import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ecommerceService } from '../../services/ecommerceService';

// Async thunks
export const fetchRecentlyViewed = createAsyncThunk(
  'ecommerceRecentlyViewed/fetchRecentlyViewed',
  async (limit = 10, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.getRecentlyViewed(limit);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const trackProductView = createAsyncThunk(
  'ecommerceRecentlyViewed/trackProductView',
  async (data, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.trackProductView(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const clearRecentlyViewed = createAsyncThunk(
  'ecommerceRecentlyViewed/clearRecentlyViewed',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.clearRecentlyViewed();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const removeRecentlyViewed = createAsyncThunk(
  'ecommerceRecentlyViewed/removeRecentlyViewed',
  async (recentlyViewedId, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.removeRecentlyViewed(recentlyViewedId);
      return { recentlyViewedId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const mergeRecentlyViewedSession = createAsyncThunk(
  'ecommerceRecentlyViewed/mergeSession',
  async (sessionKey, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.mergeRecentlyViewedSession(sessionKey);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Slice
const ecommerceRecentlyViewedSlice = createSlice({
  name: 'ecommerceRecentlyViewed',
  initialState: {
    products: [],
    loading: false,
    error: null,
    tracking: false,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch recently viewed
    builder
      .addCase(fetchRecentlyViewed.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecentlyViewed.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload.results || action.payload;
      })
      .addCase(fetchRecentlyViewed.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Track product view
    builder
      .addCase(trackProductView.pending, (state) => {
        state.tracking = true;
      })
      .addCase(trackProductView.fulfilled, (state) => {
        state.tracking = false;
      })
      .addCase(trackProductView.rejected, (state, action) => {
        state.tracking = false;
        state.error = action.payload;
      });

    // Clear recently viewed
    builder
      .addCase(clearRecentlyViewed.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(clearRecentlyViewed.fulfilled, (state) => {
        state.loading = false;
        state.products = [];
      })
      .addCase(clearRecentlyViewed.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Remove recently viewed item
    builder
      .addCase(removeRecentlyViewed.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeRecentlyViewed.fulfilled, (state, action) => {
        state.loading = false;
        state.products = state.products.filter(
          (product) => product.id !== action.payload.recentlyViewedId
        );
      })
      .addCase(removeRecentlyViewed.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Merge session
    builder
      .addCase(mergeRecentlyViewedSession.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(mergeRecentlyViewedSession.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(mergeRecentlyViewedSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError } = ecommerceRecentlyViewedSlice.actions;
export default ecommerceRecentlyViewedSlice.reducer;
